<template>
  <div
    class="pivot-table-wraper"
    ref="tableWraperRef"
  >
    <div ref="searchRef">
      <Search
        v-if="
          config?.search_config &&
          config.search_config.length > 0 &&
          !enable_advanced_search_multigroups
        "
        :searchConfig="config?.search_config || []"
        :autoRefreshOnSearchChange="config?.auto_refresh_on_search_change"
        v-model:searchConditions="searchConditions"
        :event-bus="selfEventBus"
        :searchType="search_type"
        :searchDisplayType="config?.search_display_type"
        :searchColumnCount="config?.search_column_count"
        :viewSearchConfig="viewSearchConfig"
        :disabledShowSearchGroupName="config?.disabledShowSearchGroupName"
        @viewSearchConfigChange="viewSearchConfigChange"
        @change="handleSearchChange"
      ></Search>
      <SearchFormGroup
        v-if="
          config?.search_config &&
          config.search_config.length > 0 &&
          enable_advanced_search_multigroups
        "
        :searchConfig="config?.search_config || []"
        :autoRefreshOnSearchChange="config?.auto_refresh_on_search_change"
        v-model:searchGroups="searchGroups"
        :event-bus="selfEventBus"
        :searchType="search_type"
        :searchDisplayType="config?.search_display_type"
        :searchColumnCount="config?.search_column_count"
        :disabledShowSearchGroupName="config?.disabledShowSearchGroupName"
        @change="handleSearchChange"
      ></SearchFormGroup>
    </div>
    <a-card
      ref="elRef"
      :body-style="{ padding: 0 }"
    >
      <TableHeader
        :config="config"
        :auth_batch_update_list="auth_batch_update_list"
        :auth_head_btn_list="auth_head_btn_list"
        :dataSource="dataSource"
        :current_org="current_org"
        :router="router"
        :selfEventBus="selfEventBus"
        :loading="loading"
        :screenState="screenState"
        v-model:tableSize="state.tableSize"
        v-model:settingOpen="settingOpen"
        :columns="columns"
        :view_key="view_key"
        :view_flag_name="config.title"
        :is_view="is_view"
        :refreshType="refreshType"
        :pivotConfig="pivotConfig"
        :component_type="'pivot_table'"
        @refresh="refresh"
        @export="handleExportExcel"
        @setFull="setFull"
        @exitFull="exitFull"
        @pivotConfigChange="onPivotConfigChange"
        @heightChange="h => (headerHeight = h)"
        @refreshTypeChange="handleRefreshTypeChange"
        @tableSizeChange="handleTableSizeChange"
      >
        <template #header_bar_before_title>
          <slot name="header_bar_before_title"></slot>
        </template>
        <template #header_bar_after_title>
          <slot name="header_bar_after_title"></slot>
        </template>
        <template #header_bar_before_btn>
          <slot name="header_bar_before_btn"></slot>
        </template>
        <template #header_bar_after_btn>
          <slot name="header_bar_after_btn"></slot>
        </template>
      </TableHeader>
      <div
        class="pivot-vtable-container"
        :style="{ height: tableHeight + 'px' }"
      >
        <div
          v-if="loading && !config?.disabled_table_loading"
          class="pivot-vtable-loading"
        >
          <a-spin />
          <div
            v-if="progressPercent > 0"
            class="pivot-progress"
          >
            <a-progress
              :percent="progressPercent"
              :size="[80, 14]"
              stroke-color="#1677ff"
            />
          </div>
        </div>
        <template v-if="hasPivotConfig">
          <PivotTable
            v-if="dataSource?.length > 0 || !loading"
            ref="pivotTableRef"
            :options="vtableOptions"
            :records="processedRecords"
            @onDropdownMenuClick="handleMenuClick"
            @onResizeColumnEnd="handleResizeColumnEnd"
            @onDblClickCell="handleDoubleClick"
          >
            <PivotColumnDimension
              v-for="(col, index) in effectiveColumns"
              :key="`col-${col.key}`"
              :dimensionKey="col.key"
              :title="col.label"
            />
            <PivotRowDimension
              v-for="(row, index) in effectiveRows"
              :key="`row-${row.key}`"
              :dimensionKey="row.key"
              :title="row.label"
            />
            <PivotIndicator
              v-for="val in displayIndicators"
              :key="`val-${val.id}`"
              :indicatorKey="val.id"
              :title="val.title"
              :format="(rec: any) => formatIndicatorValue(rec, val)"
            />
            <PivotCorner :titleOnDimension="'row'" />
            <Menu
              :contextMenuItems="contextMenuItems"
              :renderMode="'html'"
            />
          </PivotTable>
        </template>

        <div
          v-else
          class="pivot-empty-tip"
        >
          点击右上角"设置"按钮配置透视表字段
        </div>
      </div>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import dayjs from 'dayjs';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { filterConditions, genID } from 'liyu-pc-base/utils/function';
import { useFullscreen } from 'liyu-pc-base/utils/hooks/useFullscreen';
import { debounce, isArray, isEqual } from 'lodash';
import mitt from 'mitt';
import { computed, onMounted, onUnmounted, provide, reactive, ref, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  PivotTable,
  PivotColumnDimension,
  PivotRowDimension,
  PivotIndicator,
  PivotCorner,
  Menu,
} from '@visactor/vue-vtable';
import { TableExportPlugin } from '@visactor/vtable-plugins';
import Search from './search.vue';
import SearchFormGroup from './SearchFormGroup.vue';
import TableHeader from './tableHeader.vue';
import { formatPivotValue } from './func';
import { useRequiredConditionStore, required_condition_event_obj } from 'liyu-pc-base/init-plugin';
import { useI18n } from 'vue-i18n';
import { useEnv, useUserStore } from 'liyu-pc-base/init-plugin';
import {
  Column,
  SearchConditionItem,
  SearchGroupItem,
  Table,
  TableProState,
  ViewData,
  ViewSearchItem,
  PivotConfig,
  PivotFieldConfig,
  RefreshType,
} from './typing';
import {
  fetchViewsData,
  getDefaultSearchOperator,
  getParamsCount,
  tableProStateSymbol,
  combineLabel,
} from './utils';
import { useView } from './hooks/view';
import { query_view_settings_list } from 'liyu-pc-base/api/view-model';
import { ConditionOperator } from 'liyu-pc-base/api/typing.js';

const props = defineProps({
  config: {
    type: Object as () => Table,
    required: true,
    default: () => ({
      id: '',
      title: '',
      is_normal_model: true,
      modelType: null,
      list_url: '',
      columns: [],
    }),
  },
  eventBus: {
    type: Object,
    required: false,
  },
});

interface Emits {
  (e: 'dataChange', params: { data: any[]; current: number; total: number }): void;
  (
    e: 'filterForIdKey',
    params: {
      filter_id_key: string;
      filterList: { label: string; value: string }[];
      matchValueLabel: string;
    },
  ): void;
  (e: 'filterForIdKeyStart'): void;
}
const emit = defineEmits<Emits>();
const { t } = useI18n();
const env = useEnv();
const requiredConditionStore = useRequiredConditionStore();
const userStore = useUserStore();
const userid = userStore.userid;
const current_org = userStore.currentOrg;
const VIEW_SYSTEM_EDIT = 'view_system_edit';
const is_view_system_edit_auth = ref(userStore.CHECK_AUTH(VIEW_SYSTEM_EDIT));
const router = useRouter();
// 使用 shallowRef 而非 ref：避免 Vue 对超大数组做深度响应式代理（deep proxy 会在大数据量下
// 把内存放大数倍并最终导致 Chrome 渲染进程 OOM、标签页崩溃、错误代码 5）。VTable 只读 records，
// 不需要逐行响应式，shallowRef 仅追踪 .value 本身即可。
const dataSource = shallowRef<any[]>([]);
// 后端下发的行/列表头树（rowTree / columnTree）：由 Rust 按各维度排序规则独立排序生成，
// 覆盖 grid / tree / grid-tree 三种模式，使行、列顺序以后端数据为准（含 sort_by / sort_process_func /
// sort_order / 空值沉底）。此前列方向无独立轴，是「列字段不能排序」的根因。
const rowTreeData = ref<any[] | null>(null);
const columnTreeData = ref<any[] | null>(null);
const loading = ref(false);
const progressPercent = ref(0);
const selfEventBus = mitt();
const searchConditions = ref<SearchConditionItem[]>([]);
const searchGroups = ref<SearchGroupItem[]>([]);
const enable_advanced_search_multigroups = computed(
  () => !!props.config.enable_advanced_search_multigroups,
);

let loadDataId = null;
// 并发保护：上一次全量拉取尚未结束时，忽略本次刷新点击，避免多份全量数据在内存中叠加导致 OOM 崩溃
let isFetching = false;
const tableWraperRef = ref(null);
const tableWraperHeight = ref(null);
const searchRef = ref(null);
const searchHeight = ref(null);
const headerHeight = ref(0);
const pivotTableRef = ref<any>(null);
const auth_batch_update_list = ref([]);
const auth_head_btn_list = ref<any[]>([]);
const auth_btn_list = ref<any[]>([]);
const refreshType = ref(props.config.refreshType);
const settingOpen = ref(false);

// 透视表配置
const pivotConfig = ref<PivotConfig>({
  rows: [],
  columns: [],
  values: [],
  indicatorsAsCol: true,
  rowHierarchyType: 'grid',
  frozenRowCount: 0,
  frozenColCount: 0,
  rowShowGrandTotals: true,
  rowShowSubTotals: true,
  columnShowGrandTotals: true,
  columnShowSubTotals: true,
});

// ============ Excel 导出插件（仅透视表使用）============
// 插件实例保持稳定引用，避免随 vtableOptions 重算反复创建导致重复注册
const getExportFileName = () => {
  const title = props.config?.title || 'pivot-table';
  return `${title}_${dayjs().format('YYYYMMDD_HHmmss')}`;
};

const tableExportPlugin = new TableExportPlugin({
  exportExcelOptions: {
    downloadFile: true, // 自动下载文件
    fileName: getExportFileName(), // 文件名：标题_时间戳
    exportAllData: true, // 导出全部数据（非仅当前渲染页）
  },
});

/**
 * 触发 Excel 导出：调用 VTable 实例上的 exportToExcel（由 TableExportPlugin 注入）
 */
const handleExportExcel = () => {
  const instance = pivotTableRef.value?.vTableInstance;
  if (!instance) {
    console.warn('[pivotTable] VTable 实例尚未就绪，无法导出 Excel');
    return;
  }
  if (typeof instance.exportToExcel !== 'function') {
    console.warn('[pivotTable] 未检测到导出插件，请确认 TableExportPlugin 已注册');
    return;
  }
  try {
    instance.exportToExcel();
  } catch (e) {
    console.error('[pivotTable] 导出 Excel 失败:', e);
  }
};

const getAuthBtn = () => {
  auth_btn_list.value = userStore.CHECK_AUTH_BY_BTN_LIST(props.config?.btn_list || []) || [];
  auth_head_btn_list.value =
    userStore.CHECK_AUTH_BY_BTN_LIST(props.config?.head_btn_list || []) || [];
  auth_batch_update_list.value =
    userStore.CHECK_AUTH_BY_BTN_LIST(props.config?.batch_update_list || []) || [];
};
getAuthBtn();

const view_key = computed(() => {
  return `table_pro_${props.config.key}`;
});

const state = reactive({
  current: 1,
  pageSize: props.config?.pageSize || props.config?.query_params?.pageSize || 50000,
  total: 0,
  tableSize: 'small',
  tableHead: null,
  sum_data: null,
});

useResizeObserver(tableWraperRef, entries => {
  tableWraperHeight.value = entries[0].contentRect.height;
});
useResizeObserver(searchRef, entries => {
  searchHeight.value = entries[0].contentRect.height;
});

const search_type = computed(() => {
  if (props.config.search_type === 'simple') {
    return props.config.search_type;
  } else {
    return 'advanced';
  }
});

const is_view = computed(() => {
  return props.config?.is_view !== false;
});

const viewSearchConfig = ref<Record<string, ViewSearchItem>>({});

/**
 * 透视表组件已改为「后端预聚合」模式：必须配置 pivot_table_url（不再兼容 list_url）。
 * 前端不再分页拉全量明细做前端聚合，而是把透视配置发给后端，
 * 由后端 pandas 计算「扁平 records（含全边距小计/总计）」，前端 VTable 直接展示。
 */

/**
 * 前端透视表聚合类型 → 后端 pandas 聚合类型映射
 * ----------------------------------------------------------------
 * 前端透视表配置（pivotConfig.vue）使用 VTable 词汇：
 *   COUNT=计数(所有非空)、COUNTNUM=数值计数、VARP/STDP=总体方差/总体标准偏差；
 * 后端（views_pivot_table.py AGG_MAP）使用 pandas 词汇：
 *   COUNTA=计数(所有非空)、COUNT=数值计数、POPVAR/POPSTD。
 * 因此发送给后端前需按此表转换聚合类型标识。
 */
const AGG_TYPE_TO_BACKEND: Record<string, string> = {
  SUM: 'SUM',
  COUNT: 'COUNTA',
  COUNTNUM: 'COUNT',
  AVG: 'AVG',
  MIN: 'MIN',
  MAX: 'MAX',
  PRODUCT: 'PRODUCT',
  VAR: 'VAR',
  VARP: 'POPVAR',
  STD: 'STD',
  STDP: 'POPSTD',
  NONE: 'NONE',
};

const getQueryParams = ({
  type = 'pivot_table',
  viewData = null,
  matchFields = null,
  matchValueId = null,
} = {}) => {
  const params: any = {
    ...(props.config.query_params || {}),
  };

  // 后端预聚合模式：构造透视表配置发给后端，由 pandas 计算扁平 records（含全边距小计/总计）
  if (type === 'pivot_table') {
    params['pivot_table_config'] = pivotConfig.value;
  } else if (type === 'pivot_table_match') {
    params['pivot_table_config'] = pivotConfig.value;
    params['match_fields'] = matchFields;
    params['match_value_id'] = matchValueId;
  }

  if (search_type.value === 'advanced') {
    if (enable_advanced_search_multigroups.value) {
      const searchGroupConds = (viewData ? viewData.searchGroups || [] : searchGroups.value)
        .map(group => filterConditions(group.conditions))
        .filter(group => group.length > 0);
      const baseConds = filterConditions(props.config.base_conditions);
      const conditionsList: any[] = [];
      if (searchGroupConds.length > 0) {
        conditionsList.push({ op: 'and', children: searchGroupConds });
      }
      if (baseConds.length > 0) {
        conditionsList.push({ op: 'and', children: baseConds });
      }
      params['conditions'] = conditionsList;
    } else {
      const conditions = [
        ...filterConditions(props.config.base_conditions),
        ...filterConditions(viewData ? viewData.searchConditions || [] : searchConditions.value),
      ];
      params['conditions'] = [conditions];
    }
  } else if (search_type.value === 'simple') {
    for (const item of filterConditions(
      viewData ? viewData.searchConditions || [] : searchConditions.value,
    )) {
      const v_count = getParamsCount(item.operator, item);
      let value = item.value;
      if (v_count === 1) {
        value = value[0];
      }
      params[item.field_name] = value;
    }
  }

  if (props.config.get_query_params_func) {
    const newParams = props.config.get_query_params_func({ params, type });
    if (newParams) {
      Object.assign(params, newParams);
    }
  }

  const requiredConditionObj = getRequiredConditionObj();
  if (requiredConditionObj) {
    params['required_condition'] = requiredConditionObj;
  }
  if (props.config.get_db_alias) {
    params['db_alias'] = props.config.get_db_alias();
  }
  if (props.config.pivot_table_max_records) {
    params['max_records'] = props.config.pivot_table_max_records;
  }

  return params;
};

const [elRef, screenState, { setFull, exitFull }] = useFullscreen();

/**
 * 后端预聚合模式的数据获取：把透视配置发给后端，取回扁平 records（含全边距小计/总计）直接展示。
 * 与前端聚合模式的区别：不再分页拉全量明细、不做前端聚合，records 已按维度 key + 指标 id 聚合好。
 */
const getBackendPivotData = async (currentLoadId: symbol) => {
  if (!hasPivotConfig.value) {
    // 无任何行/列/值配置：无需请求，清空数据
    dataSource.value = [];
    state.current = 1;
    state.total = 0;
    return;
  }
  const params = getQueryParams({ type: 'pivot_table' });
  dataSource.value = [];
  const url = props.config.pivot_table_url as string;
  let response: any;
  try {
    if (props.config.query_data_func) {
      response = await props.config.query_data_func(params);
    } else {
      response = await getApiData(params, url.replace('__tree', ''), url.includes('__tree'), {
        api_log: `pivotTable ${props.config.key} ${props.config.title}`,
      });
    }
  } catch (error: any) {
    throw error;
  }
  if (loadDataId !== currentLoadId) {
    return;
  }
  // 后端返回 { records, rowTree, columnTree }：records 为扁平数据（每组分组一条 + 追加的合计/小计记录）；
  // rowTree / columnTree 为后端按排序规则独立排序好的行/列表头树，供 VTable 直接消费。
  const records = response.records || response.data || [];
  const totalCount = response.total ?? records.length;
  rowTreeData.value = response.rowTree || null;
  columnTreeData.value = response.columnTree || null;
  if (props.config.process_data_func) {
    dataSource.value = props.config.process_data_func(records);
  } else {
    dataSource.value = records;
  }
  state.current = 1;
  state.total = totalCount;
  emit('dataChange', {
    data: dataSource.value,
    current: 1,
    total: totalCount,
  });
};

const getData = async () => {
  if (!props.config.pivot_table_url) {
    // 透视表组件已改为后端预聚合模式，必须配置 pivot_table_url（不再兼容 list_url）
    return;
  }
  if (isFetching) {
    // 已有请求在途，忽略重复刷新，防止内存叠加与标签页崩溃
    return;
  }
  isFetching = true;
  const currentLoadId = Symbol();
  loadDataId = currentLoadId;
  try {
    loading.value = true;
    progressPercent.value = 0;
    await getBackendPivotData(currentLoadId);
  } catch (error: any) {
    throw error;
  } finally {
    isFetching = false;
    if (loadDataId !== currentLoadId) {
      return;
    }
    loading.value = false;
    progressPercent.value = 0;
  }
};

const delayGetDate = debounce(() => getData(), 500);

// ============ VTable PivotTable 渲染 (Vue 组件方式) ============

/**
 * 是否有有效的透视表配置
 */
const hasPivotConfig = computed(() => {
  const valueCount =
    (pivotConfig.value.values || []).filter(v => !v.hidden).length +
    (pivotConfig.value.aggComputedValues || []).length;
  return (
    valueCount > 0 &&
    (pivotConfig.value.rows.length > 0 || pivotConfig.value.columns.length > 0)
  );
});

/**
 * 实际展示的指标清单：非隐藏的值字段 + 聚合计算值。
 * 隐藏的值字段仍参与后端聚合（并可被聚合计算值引用），但不在此展示。
 * 每项含 id / 标题 / 显示格式，供 VTable indicator 与格式化复用。
 */
interface DisplayIndicator {
  id: string;
  title: string;
  formatFun?: string;
  formatPattern?: string;
}
const displayIndicators = computed<DisplayIndicator[]>(() => {
  const out: DisplayIndicator[] = [];
  for (const v of pivotConfig.value.values || []) {
    if (v.hidden) continue;
    out.push({
      id: v.id,
      title: v.title || v.label || v.field || v.id,
      formatFun: v.formatFun,
      formatPattern: v.formatPattern,
    });
  }
  for (const a of pivotConfig.value.aggComputedValues || []) {
    out.push({
      id: a.id,
      title: a.name,
      formatFun: a.formatFun,
      formatPattern: a.formatPattern,
    });
  }
  return out;
});

/**
 * 行/列维度的「数据处理函数」应用
 * ----------------------------------------------------------------
 * 每个维度映射为一个带稳定 key 的对象：key 优先用 id（唯一，支持同字段重复添加），
 * 缺失时回退到 `field__索引`。VTable 的 dimensionKey 使用该 key，而非原始 field，
 * 这样即使同一原始字段被添加多次（如「日期→年」「日期→月」），也能彼此区分。
 */
// 维度组展示标签：优先用配置显示名 label，缺省按字段名以下划线连接（与配置组件默认预览一致）
const dimGroupLabel = (d: PivotFieldConfig): string => {
  if (d.label) return d.label;
  const labelList = d.fields.map((fld: any) => fld.label || fld.field);
  return combineLabel(labelList, d.linkType);
};

const effectiveRows = computed(() =>
  pivotConfig.value.rows.map(r => {
    // 维度键须与后端记录键一致：后端 DimSpec 的 resolved_name = id 或首字段名
    const key = r.id || (r.fields && r.fields.length ? r.fields[0].field : '');
    return {
      ...r,
      key,
      label: dimGroupLabel(r),
    };
  }),
);
const effectiveColumns = computed(() =>
  pivotConfig.value.columns.map(c => {
    const key = c.id || (c.fields && c.fields.length ? c.fields[0].field : '');
    return {
      ...c,
      key,
      label: dimGroupLabel(c),
    };
  }),
);

/**
 * 派生后的 records：后端预聚合模式已按维度 key（含 processFunc 派生列，键为维度 id）输出，
 * 且合计记录本就缺失部分维度字段；前端不再二次派生，直接透传后端 records。
 */
const processedRecords = computed<any[]>(() => {
  return dataSource.value;
});

/**
 * 右键菜单项
 */
const contextMenuItems = [{ text: '复制', menuKey: 'copy' }];

/**
 * VTable 表格级别选项（不含 rows/columns/indicators/menu，这些由插槽子组件提供）
 */
const vtableOptions = computed(() => {
  // 聚合规则：后端预聚合模式，每个叶子交叉分组在后端已聚合为「仅一条」记录（值键为指标 id），
  // 前端 VTable 对单条记录做 SUM 即等于该值本身；合计/小计单元格则由后端追加的
  // 「缺失尾部维度」记录直接命中 totalRecordsTree 而被 VTable 采用（见 dataset.js
  // totalStatistics/colCompute），因此聚合类型统一用 SUM，field 指向指标 id。
  const aggregationRules = displayIndicators.value.map(v => ({
    indicatorKey: v.id,
    field: v.id,
    aggregationType: 'SUM',
  }));

  // 是否为树形结构（传统树形 / 网格树形）。仅树形结构中父节点行才承载合计值。
  const isTreeLike =
    pivotConfig.value.rowHierarchyType === 'tree' ||
    pivotConfig.value.rowHierarchyType === 'grid-tree';
  const frozenRowCount = Math.max(0, Math.floor(Number(pivotConfig.value.frozenRowCount) || 0));
  const frozenColCount = Math.max(0, Math.floor(Number(pivotConfig.value.frozenColCount) || 0));

  // 后端预聚合模式：需显式声明各级小计维度，VTable 才能把后端追加的「缺失尾部维度」记录
  // 识别为对应层级的小计并直接采用其值（见 dataset.js processRecord / totalStatistics）。
  // 取「除最内层外」的所有维度键：
  //   - 行：dataset.js 对最内层维度天然跳过小计（totalStatistics 的 dimensionIndex < rows.length-1 守卫）；
  //   - 列：colCompute 无该守卫，若包含最内层列维度会在最深层生成冗余「小计」列，故必须排除最后一个。
  const rowDimKeys = effectiveRows.value.map(d => d.key);
  const colDimKeys = effectiveColumns.value.map(d => d.key);
  const rowSubTotalsDimensions = rowDimKeys.slice(0, -1);
  const colSubTotalsDimensions = colDimKeys.slice(0, -1);

  /**
   * 向后端下发的行/列表头树中「注入」小计与合计节点。
   * 原因：VTable 在传入自定义 rowTree/columnTree 时不会像推断树那样自动插入小计/合计节点
   * （dataset-pivot-table.js 第 43-44 行：customRowTree 直接走 TreeToArr2，跳过 ArrToTree 的小计插入）；
   * 而 totalStatistics 仍会按 totals 配置生成「小计/总计」单元格（key 为维度值拼接 subTotalLabel/grandTotalLabel）。
   * 若不提供对应节点，这些单元格无路径可挂 → 小计/合计不显示。
   * 本函数复刻 VTable 推断树的小计节点结构：在每个「非最内层」维度组下挂 value=小计 的子节点，
   * 并在根级追加 value=总计 的合计节点，使 key 与 totalStatistics 对齐，从而正确渲染。
   * 叶子顺序仍完全由后端树（按后端排序规则生成）决定。
   *
   * includeSubtotals=false（树形 / 网格树形模式）：仅保留合计节点，不注入独立小计子节点。
   * 原因：树形模式下小计值由「父级节点」自身承载（配合 showSubTotalsOnTreeNode=true，
   * 父节点的指标列即显示小计），若仍注入独立 value=小计 的子节点会导致该行为空白行。
   */
  const injectTotals = (
    tree: any[],
    dimKeys: string[],
    subTotalsDimensions: string[],
    showGrandTotals: boolean,
    subTotalLabel: string,
    grandTotalLabel: string,
    includeSubtotals: boolean = true,
  ): any[] => {
    if (!tree || !tree.length) return tree;
    const clone = (nodes: any[]): any[] =>
      nodes.map(n => ({ ...n, children: n.children ? clone(n.children) : [] }));
    const cloned = clone(tree);
    const subSet = new Set(subTotalsDimensions || []);
    const numDims = dimKeys.length;
    const addSub = (nodes: any[], depth: number) => {
      for (const node of nodes) {
        const origChildren = node.children || [];
        if (depth < numDims - 1 && subSet.has(node.dimensionKey)) {
          // 先递归原有子节点（避免小计节点被二次递归），再挂小计子节点
          if (origChildren.length) addSub(origChildren, depth + 1);
          node.children = origChildren.concat([
            {
              dimensionKey: dimKeys[depth + 1] ?? node.dimensionKey,
              value: subTotalLabel,
              hierarchyState: 'expand',
              children: [],
            },
          ]);
        } else if (origChildren.length) {
          addSub(origChildren, depth + 1);
        }
      }
    };
    if (includeSubtotals) addSub(cloned, 0);
    if (showGrandTotals && numDims > 0) {
      cloned.push({
        dimensionKey: dimKeys[0],
        value: grandTotalLabel,
        hierarchyState: 'expand',
        children: [],
      });
    }
    return cloned;
  };

  return {
    rowHierarchyType:
      effectiveRows.value?.length >= 2 ? pivotConfig.value.rowHierarchyType || 'grid' : 'grid',
    indicatorsAsCol: pivotConfig.value.indicatorsAsCol ?? true,
    frozenRowCount,
    frozenColCount,
    // 后端下发的行/列表头树：覆盖 grid / tree / grid-tree 全部模式，使行、列顺序以后端排序规则为准。
    // 仅当树非空才下发（无维度或空数据时退化为 VTable 默认推断）。
    ...(rowTreeData.value && rowTreeData.value.length
      ? {
          rowTree: injectTotals(
            rowTreeData.value,
            rowDimKeys,
            rowSubTotalsDimensions,
            !!pivotConfig.value.rowShowGrandTotals,
            '小计',
            '总计',
            // 网格模式：仅当用户开启「行小计」时才注入独立小计子节点；
            // 树形模式恒不注入（小计由父节点承载）。
            !isTreeLike && !!pivotConfig.value.rowShowSubTotals,
          ),
        }
      : {}),
    ...(columnTreeData.value && columnTreeData.value.length
      ? {
          columnTree: injectTotals(
            columnTreeData.value,
            colDimKeys,
            colSubTotalsDimensions,
            !!pivotConfig.value.columnShowGrandTotals,
            '小计',
            '总计',
            // 网格模式：仅当用户开启「列小计」时才注入独立小计子节点；
            // 树形模式恒不注入（小计由父节点承载）。
            !isTreeLike && !!pivotConfig.value.columnShowSubTotals,
          ),
        }
      : {}),
    // 展开层级：树节点已显式标记为 expand，此值作为兜底（grand-total 等无显式状态的节点依此展开）
    rowExpandLevel: Math.max(2, effectiveRows.value.length + 1),
    rowHierarchyIndent: 20,
    rowHierarchyTextStartAlignment: true,
    columnExpandLevel: Math.max(2, effectiveColumns.value.length + 1),
    heightMode: 'standard',
    // 使用 autoWidth 模式：未显式指定宽度的列按「列标题 + 数据」的最大内容宽度自动测量，
    // 从根本上保证列特别多时单元格内容（列标题、数据等）都能完整显示，超出容器则横向滚动。
    // 注意：此模式下 columnWidthConfig / columnWidthConfigForRowHeader 中保存的列宽仍通过
    // _widthResizedColMap 优先生效，因此「视图保存的列宽」在每次重渲染后依旧能还原（不影响 req2/req3）。
    widthMode: 'autoWidth',
    // autoFillWidth: 仅当所有列的自然内容宽度之和小于容器宽度时（即列较少），把剩余空间按比例分配到各列，
    // 让透视表自动撑满整个宽度；当列较多（内容宽度之和已超过容器）时则不填充，保留横向滚动。
    autoFillWidth: true,
    autoWrapText: false,
    defaultRowHeight: 36,
    defaultHeaderRowHeight: 36,
    defaultColWidth: 120,
    defaultHeaderColWidth: 120,
    // 列宽下限（地板值，只放大不截断）：原 100 偏宽，列特别多时短内容列会不必要地占空间。
    // 降到 60，让更多列能同时可见，同时仍保证可读性与完整显示。
    limitMinWidth: 60,
    theme: {
      // 最外围边框：加粗整个透视表的外框（innerBorder 默认 false，只画外框不画内网格）
      frameStyle: {
        borderColor: '#000000',
        borderLineWidth: 2,
        borderLineDash: [],
        cornerRadius: 4,
      },
      headerStyle: {
        bgColor: '#f5f5f5',
        color: '#333',
        fontSize: 13,
        fontFamily: 'PingFangSC, -apple-system, BlinkMacSystemFont, sans-serif',
        frameStyle: {
          borderColor: '#e8e8e8',
          borderLineWidth: 1,
        },
      },
      rowHeaderStyle: {
        bgColor: '#fafafa',
        color: '#333',
        fontSize: 13,
        fontFamily: 'PingFangSC, -apple-system, BlinkMacSystemFont, sans-serif',
        frameStyle: {
          borderColor: '#e8e8e8',
          borderLineWidth: 1,
        },
      },
      cornerHeaderStyle: {
        bgColor: '#f0f0f0',
        color: '#333',
        fontSize: 13,
        fontFamily: 'PingFangSC, -apple-system, BlinkMacSystemFont, sans-serif',
        frameStyle: {
          borderColor: '#e8e8e8',
          borderLineWidth: 1,
        },
      },
      bodyStyle: {
        color: '#333',
        fontSize: 13,
        fontFamily: 'PingFangSC, -apple-system, BlinkMacSystemFont, sans-serif',
        frameStyle: {
          borderColor: '#f0f0f0',
          borderLineWidth: 1,
        },
      },
    },
    dataConfig: {
      aggregationRules,
      totals: {
        row: {
          showGrandTotals: !!pivotConfig.value.rowShowGrandTotals,
          showSubTotals: !!pivotConfig.value.rowShowSubTotals,
          // 后端预聚合模式：声明各级行小计维度，使 VTable 采用后端提供的行小计记录
          subTotalsDimensions: rowSubTotalsDimensions,
          // 树形结构下：将合计值直接显示在父节点（非叶子 / 收起父节点）行上
          showSubTotalsOnTreeNode: true,
          grandTotalLabel: '总计',
          subTotalLabel: '小计',
        },
        column: {
          showGrandTotals: !!pivotConfig.value.columnShowGrandTotals,
          showSubTotals: !!pivotConfig.value.columnShowSubTotals,
          // 后端预聚合模式：声明各级列小计维度，使 VTable 采用后端提供的列小计记录
          subTotalsDimensions: colSubTotalsDimensions,
          showSubTotalsOnTreeNode: true,
          grandTotalLabel: '总计',
          subTotalLabel: '小计',
        },
      },
    },
    keyboardOptions: {
      copySelected: true,
    },
    // 应用已保存的列宽配置：
    // - columnWidthConfig：数值列 / 合计列（位于行表头右侧的指标列）
    // - columnWidthConfigForRowHeader：行配置产生的列（位于左侧的行表头列，如行维度标签列、行小计/总计标签列）
    ...(buildColumnWidthConfig.value ? { columnWidthConfig: buildColumnWidthConfig.value } : {}),
    ...(buildRowHeaderWidthConfig.value
      ? { columnWidthConfigForRowHeader: buildRowHeaderWidthConfig.value }
      : {}),
    // 注册 Excel 导出插件（实例稳定，不随 options 重算而重建）
    plugins: [tableExportPlugin],
    emptyTip: {
      text: '暂无数据',
    },
  };
});

/**
 * 格式化指标值
 * - duration_seconds / duration_minutes → 时间间隔；
 * - numfmt → 按值字段 formatPattern（Excel 标准格式串）经 numfmt 库格式化；
 * - 旧配置 key（'0.00' 等）自动视为 numfmt 格式串，兼容历史数据。
 * 值字段与聚合计算值共用同一套格式（均含 formatFun / formatPattern）。
 */
const formatIndicatorValue = (value: number, indicator: DisplayIndicator): string => {
  if (value === null || value === undefined) return '';
  if (indicator.formatFun) {
    return formatPivotValue(value, indicator.formatFun, indicator.formatPattern);
  }
  return String(value);
};

/**
 * 行/列表头树（rowTree / columnTree）现由后端 Rust（pivot_table_full）按各维度排序规则
 * 独立排序生成并随响应一并返回，前端仅消费 `rowTreeData` / `columnTreeData`（见 getBackendPivotData
 * 与 vtableOptions），不再在前端基于 records 推断树。这样行、列顺序都以后端数据为准
 * （含 sort_by / sort_process_func / sort_order / 空值沉底），根除了「列字段不能排序」的问题。
 */

/**
 * 处理右键菜单点击（复制）
 */
const handleMenuClick = (args: any) => {
  if (args.menuKey === 'copy') {
    const instance = pivotTableRef.value?.vTableInstance;
    if (instance) {
      const copyValue = instance.getCopyValue();
      if (copyValue) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(copyValue).catch(() => {
            execCommandCopy(copyValue);
          });
        } else {
          execCommandCopy(copyValue);
        }
      }
    }
  }
};
const handleDoubleClick = async (args: any) => {
  const colHeaderPaths = args.cellHeaderPaths?.colHeaderPaths || [];
  const rowHeaderPaths = args.cellHeaderPaths?.rowHeaderPaths || [];
  const matchFields = [];
  const dimsMap = {};
  for (const dim of [...effectiveRows.value, ...effectiveColumns.value]) {
    dimsMap[dim.key] = dim.label;
  }
  const filterList = [];
  let matchValueId = null;
  let matchValueLabel = null;
  for (const path of [...colHeaderPaths, ...rowHeaderPaths]) {
    if (!path.role && path.dimensionKey && !['小计', '总计'].includes(path.value)) {
      matchFields.push({
        id: path.dimensionKey,
        value: path.value,
      });
      filterList.push({
        label: dimsMap[path.dimensionKey],
        value: path.value,
      });
    }
    if (!path.role && path.indicatorKey && !['小计', '总计'].includes(path.value)) {
      matchValueId = path.indicatorKey;
      matchValueLabel = path.value;
    }
  }
  if (!matchFields.length) return;
  emit('filterForIdKeyStart');
  try {
    const params = getQueryParams({ type: 'pivot_table_match', matchFields, matchValueId });
    const url = props.config.pivot_table_url;
    const response = await getApiData(params, url.replace('__tree', ''), url.includes('__tree'), {
      api_log: `pivotTable ${props.config.key} ${props.config.title}`,
    });
    emit('filterForIdKey', {
      filter_id_key: response?.filter_id_key || '',
      filterList,
      matchValueLabel,
    });
  } catch {
    emit('filterForIdKey', null);
  }
};

/**
 * 兼容性复制：使用 execCommand 作为 fallback
 */
const execCommandCopy = (text: string) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } catch (e) {
    console.error('[pivotTable] copy failed:', e);
  }
  document.body.removeChild(textarea);
};

// ============ 透视表列宽调整持久化 ============
const handleResizeColumnEnd = (args: any) => {
  const instance = pivotTableRef.value?.vTableInstance;
  if (!instance) return;

  const { col } = args;
  const width = instance.getColWidth(col);

  // 取该列在表体首行处的表头路径，据此区分：
  // - rowHeader（行配置产生的列 / 行维度标签列 / 行小计·总计标签列）→ 写入 columnWidthConfigForRowHeader
  // - 其余（数值列、合计列等位于行表头右侧的指标列）→ 写入 columnWidthConfig
  const headerPath = instance.getCellHeaderPaths(col, instance.columnHeaderLevelCount);
  const isRowHeader = headerPath?.cellLocation === 'rowHeader';
  const dims = isRowHeader ? headerPath?.rowHeaderPaths : headerPath?.colHeaderPaths;
  if (!dims || !dims.length) return;

  const store = isRowHeader ? pivotRowHeaderWidths : pivotColumnWidths;
  const list = store.value;

  // 用 VTable 权威方法按「列」定位该列已有的配置项，避免 JSON.stringify 因字段顺序 / 字段集差异
  // （视图经存取后序列化再反序列化，列头路径对象的键顺序可能改变）而误判为未命中。
  // 误判未命中会导致 push 出新项，形成「旧宽度项 + 新宽度项」两条同列记录；VTable 重渲染重新解析
  // columnWidthConfig 时靠前的旧项先占住该列，新宽度被忽略 —— 表现为「拖拽后一松手就弹回原宽度」。
  // 此处命中则就地替换为规范的实时路径 + 新宽度，并把多命中的历史脏数据一并清除，确保每列至多一条记录。
  const matched: number[] = [];
  for (let i = 0; i < list.length; i++) {
    const cell = instance.getCellAddressByHeaderPaths(list[i].dimensions);
    if (cell && cell.col === col) matched.push(i);
  }
  if (matched.length) {
    // 归一化：把 dimensions 替换为本次实时取出的规范路径，后续匹配更稳定
    list[matched[0]] = { dimensions: dims, width };
    // 从后往前删除其余重复项，避免索引错位
    for (let k = matched.length - 1; k >= 1; k--) list.splice(matched[k], 1);
  } else {
    list.push({ dimensions: dims, width });
  }
  isViewChange.value = true;
};

// ============ 表头高度计算 ============

const tableHeight = computed(() => {
  if (!tableWraperHeight.value) {
    return 400;
  }
  let value = tableWraperHeight.value - searchHeight.value - headerHeight.value - 4;
  if (value < 200) {
    value = 200;
  }
  return value;
});

// ============ 列配置（供 TableHeader 列设置 UI 使用，与 list_url 数据拉取无关）============

const columns = computed<Column[]>(() => {
  const baseColumns = [...(props.config.columns || [])];
  const hasIndex = baseColumns.some(item => item.dataIndex === 'index');
  if (!hasIndex && !props.config.hide_index_column) {
    baseColumns.unshift({
      title: '#',
      dataIndex: 'index',
      key: 'index',
      maxWidth: 50,
      fixed: 'left',
      checked: true,
      autoHeight: true,
    });
  }
  return baseColumns;
});

// ============ 视图相关 ============

const viewColumnsConfig = ref<Record<string, any>>({});
const viewDataList = ref([]);
const currentView = ref<any>(null);
const isViewChange = ref(false);

// ============ 透视表列宽持久化 ============
// 数值列 / 合计列（行表头右侧的指标列）宽度
const pivotColumnWidths = ref<Array<{ dimensions: any[]; width: number }>>([]);
// 行配置产生的列（左侧行表头列，如行维度标签列、行小计/总计标签列）宽度
const pivotRowHeaderWidths = ref<Array<{ dimensions: any[]; width: number }>>([]);

/**
 * 将 pivotColumnWidths 转换为 VTable columnWidthConfig 格式
 * VTable PivotTable 要求格式: [{ dimensions: IDimensionInfo[], width: number }]
 */
const buildColumnWidthConfig = computed(() => {
  if (!pivotColumnWidths.value.length) return undefined;
  return pivotColumnWidths.value.map(item => ({
    dimensions: item.dimensions,
    width: item.width,
  }));
});

/**
 * 将 pivotRowHeaderWidths 转换为 VTable columnWidthConfigForRowHeader 格式
 * 用于保存并还原「行配置产生的列」（左侧行表头列）的宽度
 */
const buildRowHeaderWidthConfig = computed(() => {
  if (!pivotRowHeaderWidths.value.length) return undefined;
  return pivotRowHeaderWidths.value.map(item => ({
    dimensions: item.dimensions,
    width: item.width,
  }));
});

/**
 * 由维度路径生成稳定 key（仅取 VTable 判定同列所依赖的关键字段 dimensionKey/value/indicatorKey），
 * 不受对象字段顺序影响，用于去重时识别「同一列」。
 */
const widthConfigKey = (dimensions: any[]): string =>
  (dimensions || [])
    .map((d: any) => `${d?.dimensionKey ?? ''}|${d?.value ?? ''}|${d?.indicatorKey ?? ''}`)
    .join('::');

/**
 * 列宽配置去重：同一列只保留最后一条，清除历史脏数据产生的重复项。
 * 重复项会让 VTable 重渲染重新解析 columnWidthConfig 时靠前旧项先占住该列，新宽度被忽略（拖拽回弹）。
 * 纯数据操作，无需 VTable 实例，可在视图加载时调用。
 */
const dedupeWidthConfig = (items: Array<{ dimensions: any[]; width: number }>) => {
  const lastIndexByKey = new Map<string, number>();
  // 从后往前遍历，记录每个 key 最后出现的下标，并删除更靠前的重复项
  for (let i = items.length - 1; i >= 0; i--) {
    const key = widthConfigKey(items[i].dimensions);
    if (lastIndexByKey.has(key)) {
      items.splice(i, 1);
    } else {
      lastIndexByKey.set(key, i);
    }
  }
  return items;
};

const onPivotConfigChange = (newConfig: PivotConfig) => {
  pivotConfig.value = newConfig;
  isViewChange.value = true;
  // 后端预聚合模式：聚合在服务端完成，透视配置（行/列/值/聚合算法）变化必须重新请求
  delayGetDate('pivot-config-change');
};

const handleRefreshTypeChange = (type: string) => {
  refreshType.value = type as RefreshType;
  isViewChange.value = true;
};

const handleTableSizeChange = () => {
  isViewChange.value = true;
};

const handleSearchChange = () => {
  isViewChange.value = true;
};

const viewSearchConfigChange = (config: Record<string, ViewSearchItem>) => {
  viewSearchConfig.value = config;
  isViewChange.value = true;
};

const refresh = async () => {
  await getData();
};

const local_refresh = async () => {
  // PivotTable Vue 组件会自动响应 records 变化，无需手动触发渲染
};

const getRequiredConditionObj = () => {
  const required_condition = props.config.required_condition;
  if (required_condition) {
    const required_condition_obj: Record<string, any> = {};
    required_condition.forEach((item: any) => {
      const value = (requiredConditionStore as any).value_map[item.key];
      if (value !== undefined && value !== null) {
        required_condition_obj[item.field_name] = value;
      }
    });
    return required_condition_obj;
  } else {
    return null;
  }
};

// ============ 初始化 ============

const init = async () => {
  const tmpSearchConditions: SearchConditionItem[] = [];
  if (props.config?.search_config) {
    props.config.search_config.forEach(configItem => {
      if (configItem.default_value) {
        let value = configItem.default_value;
        if (!isArray(value)) {
          value = [value];
        }
        tmpSearchConditions.push({
          field_name: configItem.field_name,
          value: value,
          operator: getDefaultSearchOperator(configItem) as ConditionOperator,
          format: configItem.format || 'input',
          class_name: configItem.class_name || 'str',
        });
      }
    });
  }

  searchConditions.value = tmpSearchConditions;
  if (enable_advanced_search_multigroups.value) {
    if (props.config.default_advanced_search_multigroups) {
      searchGroups.value = props.config.default_advanced_search_multigroups;
    } else {
      searchGroups.value = [
        {
          id: genID(),
          conditions: [...tmpSearchConditions],
          viewSearchConfig: {},
        },
      ];
    }
    searchConditions.value = [];
  }

  if (is_view.value) {
    if (currentView.value?.id !== 'no-view') {
      currentView.value = null;
      await fetchViews();
      if (props.config?.view_uid) {
        const findView = viewDataList.value.find((item: any) => item.uid == props.config?.view_uid);
        if (findView) {
          setViewData(findView.data);
          currentView.value = findView;
        } else {
          const res: any = await query_view_settings_list({
            org_id: current_org.id,
            is_active: true,
            uid: props.config?.view_uid,
          });
          if (res?.length) {
            currentView.value = { ...res[0], is_other_user_view: true };
          }
        }
      }
      if (!currentView.value && viewDataList.value?.length) {
        const tmpData = [...viewDataList.value];
        const getSortValue = (item: any) => {
          let value = item.sort || 0;
          if (item.id !== currentView.value?.id) {
            value *= 1000000;
          }
          if (!item.is_default) {
            value *= 10000;
          }
          if (!item.user_id) {
            value *= 1000;
          }
          return value;
        };
        tmpData.sort((a, b) => getSortValue(a) - getSortValue(b));
        setViewData(tmpData[0].data);
        currentView.value = tmpData[0];
      }
    }
  }
  await getData();
};

const eventReinit = async () => {
  isViewChange.value = false;
  await init();
};

const eventReload = async () => {
  await getData();
};

const getViewData = (): ViewData => {
  const viewData: any = {
    columns: viewColumnsConfig.value,
    searchConditions: searchConditions.value,
    tableSize: state.tableSize,
    refreshType: refreshType.value,
    searchConfig: viewSearchConfig.value,
    pivotConfig: pivotConfig.value,
    pivotColumnWidths: pivotColumnWidths.value,
    pivotRowHeaderWidths: pivotRowHeaderWidths.value,
  };
  if (enable_advanced_search_multigroups.value) {
    viewData.searchGroups = searchGroups.value;
    if (searchGroups.value.length > 0) {
      viewData.searchConfig = searchGroups.value[0].viewSearchConfig;
    }
  }
  if (props.config.get_view_data_func) {
    const newViewData = props.config.get_view_data_func({ viewData });
    if (newViewData) {
      Object.assign(viewData, newViewData);
    }
  }
  return viewData;
};

const setViewData = async (viewData: any) => {
  if (viewData === null) {
    await init();
    isViewChange.value = false;
    return;
  }
  if (viewData.columns) {
    viewColumnsConfig.value = viewData.columns;
  }
  if (enable_advanced_search_multigroups.value) {
    if (viewData.searchGroups && viewData.searchGroups.length > 0) {
      searchGroups.value = viewData.searchGroups;
    } else if (viewData.searchConditions) {
      searchGroups.value = [
        {
          id: genID(),
          conditions: viewData.searchConditions,
          viewSearchConfig: viewData.searchConfig || {},
        },
      ];
    }
    if (searchGroups.value.length === 0) {
      searchGroups.value = [
        {
          id: genID(),
          conditions: [],
          viewSearchConfig: {},
        },
      ];
    }
    searchConditions.value = [];
  } else {
    if (viewData.searchConfig) {
      viewSearchConfig.value = viewData.searchConfig;
    }
    if (viewData.searchConditions) {
      searchConditions.value = viewData.searchConditions;
    }
  }
  if (viewData.tableSize) {
    state.tableSize = viewData.tableSize;
  }
  if (viewData.refreshType) {
    refreshType.value = viewData.refreshType;
  }
  // 加载透视表配置
  if (viewData.pivotConfig) {
    pivotConfig.value = viewData.pivotConfig;
  }
  // 加载透视表列宽配置
  if (viewData.pivotColumnWidths) {
    pivotColumnWidths.value = dedupeWidthConfig(viewData.pivotColumnWidths);
  }
  // 加载行表头列（行配置产生的列）宽度配置
  if (viewData.pivotRowHeaderWidths) {
    pivotRowHeaderWidths.value = dedupeWidthConfig(viewData.pivotRowHeaderWidths);
  }
  if (props.config.set_view_callback) {
    props.config.set_view_callback({ viewData });
  }
  await getData();
  isViewChange.value = false;
};

const fetchViews = async () => {
  const data = await fetchViewsData(
    view_key.value,
    getQueryParams,
    props.config.count_url,
    props.config,
  );
  viewDataList.value = data;
};

const { saveView, addView } = useView({
  currentView,
  getViewData,
  isViewChange,
  userid,
  view_project__flag: env.VITE_APP_VIEW_PROJECT_FLAG,
  view_menu__flag: view_key.value,
  view_menu__name: props.config.title,
  fetchViews,
  is_auth: is_view_system_edit_auth,
  t,
});

provide<TableProState>(tableProStateSymbol, {
  setViewData,
  getViewData,
  fetchViews,
  viewDataList,
  currentView,
  isViewChange,
  config: props.config,
});

onMounted(async () => {
  if (!props.config.key) {
    console.error('pivotTable config 未配置 key');
  }
  if (!props.config.title) {
    console.error('pivotTable config 未配置 title');
  }
  required_condition_event_obj().on('change', handleRequiredConditionChange);
  selfEventBus.on('refresh', refresh);
  selfEventBus.on('local_refresh', local_refresh);
  selfEventBus.on('reinit', eventReinit);
  selfEventBus.on('saveView', saveView);
  selfEventBus.on('saveNewView', addView);
  if (props.eventBus) {
    props.eventBus.on('reinit', eventReinit);
    props.eventBus.on('refresh', refresh);
    props.eventBus.on('local_refresh', local_refresh);
    props.eventBus.on('reload_table', eventReload);
  }
  if (props.config.tableSize) {
    state.tableSize = props.config.tableSize;
  }
  await init();
});

const handleRequiredConditionChange = (key: string) => {
  if (props.config.required_condition) {
    const item = props.config.required_condition.find((item: any) => item.key === key);
    if (item) {
      getData();
    }
  }
};

onUnmounted(() => {
  selfEventBus.off('refresh', refresh);
  selfEventBus.off('local_refresh', local_refresh);
  selfEventBus.off('reinit', eventReinit);
  selfEventBus.off('saveView', saveView);
  selfEventBus.off('saveNewView', addView);
  if (props.eventBus) {
    props.eventBus.off('reinit', eventReinit);
    props.eventBus.off('refresh', refresh);
    props.eventBus.off('local_refresh', local_refresh);
    props.eventBus.off('reload_table', eventReload);
  }
});

watch(
  () => props.config.query_params,
  () => {
    delayGetDate('watch-query-params');
  },
  { deep: true },
);

watch(
  () => props.config.base_conditions,
  () => {
    delayGetDate('watch-base-conditions');
  },
  { deep: true },
);

defineExpose({
  getViewData,
  setViewData,
  config: props.config,
  columns,
  fetchViews,
  viewDataList,
  currentView,
  isViewChange,
  pivotConfig,
  refresh,
  init,
  dataSource,
  get vTableInstance() {
    return pivotTableRef.value?.vTableInstance || null;
  },
  settingOpen,
});
</script>

<style lang="less" scoped>
.pivot-table-wraper {
  height: 100%;
  overflow: hidden;
}
.pivot-vtable-container {
  position: relative;
  width: 100%;
  overflow: hidden;

  .pivot-vtable-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    background: rgba(255, 255, 255, 0.6);
    flex-direction: column;
    gap: 16px;

    .pivot-progress {
      width: 200px;
    }
  }

  .pivot-empty-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
    font-size: 14px;
  }
}
</style>
